import { FunctionComponent, JSX, useEffect, useState } from 'react'
import { Child } from '@/types/Child.ts'
import { ParentDaycare } from '@/types/Parent.ts'
import styles from './taskAndNotesParent.module.scss'
import Title from '@/components/_custom/Title/title.tsx'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { ArrowForward, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { useLazyQuery } from '@apollo/client'
import { GET_TASK_NOTES_FOR_PROFILE } from '@/modules/API/taskNote/getTaskNotes.ts'
import { TaskNote, TaskNoteProfile } from '@/types/TaskNote.ts'
import Loading from '@/components/Loading/Loading.tsx'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import AddTasksNotesDialog from '../../../taskNote/Dialog/addTasksNotesDialog.tsx'
import TaskNoteComponent from '../../../taskNote/taskNoteComponent/taskNoteComponent.tsx'

interface TaskAndNotesParentProps {
    daycare: ParentDaycare
    parentId: number
    childs: Child[]
    parentName: string
    canUpdate: boolean
    handleRefetchParent: () => void
}

const TaskAndNotesParent: FunctionComponent<TaskAndNotesParentProps> = ({
    daycare,
    parentId,
    childs,
    parentName,
    canUpdate,
    handleRefetchParent
}: TaskAndNotesParentProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [tasksAndNotesParents, setTasksAndNotesParents] = useState<TaskNoteProfile>()
    const [otherTasksAndNotesCount, setOtherTasksAndNotesCount] = useState<number>(0)

    const [showMore, setShowMore] = useState<boolean>(true)

    const [addTasksNotesDialogOpen, setAddTasksNotesDialogOpen] = useState<boolean>(false)

    const [getTaskNotesForProfile, { loading: loadingTaskNotes, data: dataTaskNotes }] = useLazyQuery(
        GET_TASK_NOTES_FOR_PROFILE,
        {
            variables: {
                daycareId: daycare.daycareId,
                parentId
            }
        }
    )

    useEffect(() => {
        if (dataTaskNotes && dataTaskNotes.getTaskNotesForProfile) {
            setTasksAndNotesParents(dataTaskNotes.getTaskNotesForProfile)
            if (dataTaskNotes.getTaskNotesForProfile.count > 2) {
                setOtherTasksAndNotesCount(dataTaskNotes.getTaskNotesForProfile.count - 2)
            }
        }
    }, [dataTaskNotes])

    useEffect(() => {
        getTaskNotesForProfile()
    }, [daycare])

    return (
        <div className={styles.Form}>
            <div className={styles.TitleAndButton}>
                <div className={styles.Title} onClick={() => setShowMore(!showMore)}>
                    <Title text={t('tasksAndNotes')}></Title>
                    {showMore ? (
                        <KeyboardArrowUp className={styles.Icon} />
                    ) : (
                        <KeyboardArrowDown className={styles.Icon} />
                    )}
                </div>
                {canUpdate && showMore && (
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setAddTasksNotesDialogOpen(true)}
                        />
                    </div>
                )}
            </div>
            {loadingTaskNotes && <Loading overall={false} />}
            {showMore && (
                <>
                    {tasksAndNotesParents && tasksAndNotesParents.count > 0 ? (
                        <div className={styles.TaskNoteContainer}>
                            {tasksAndNotesParents.taskNotes.map(
                                (taskNote: TaskNote): JSX.Element => (
                                    <div
                                        className={
                                            tasksAndNotesParents.taskNotes.length >= 2
                                                ? styles.TaskNote
                                                : styles.TaskNoteFullWidth
                                        }
                                        key={taskNote.id}
                                    >
                                        <TaskNoteComponent
                                            taskNote={taskNote}
                                            isParent={false}
                                            isRpeOrLaep={daycare.isRpeOrLaep}
                                            canUpdate={canUpdate}
                                            daycareId={daycare.daycareId}
                                            handleRefetch={handleRefetchParent}
                                            childs={childs}
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    ) : (
                        <div className={styles.NoTasksNotes}>{t('noTasksAndNotes')}</div>
                    )}
                    {tasksAndNotesParents && otherTasksAndNotesCount > 0 && (
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                className={styles.Button}
                                customText={
                                    otherTasksAndNotesCount +
                                    ' ' +
                                    `${otherTasksAndNotesCount === 1 ? t('other').toLowerCase() : t('others').toLowerCase()}` +
                                    ' ' +
                                    `${t('tasksAndNotes').toLowerCase()}`
                                }
                                customIcon={<ArrowForward />}
                                onClick={() => navigate('/taskNote?parentId=' + parentId)}
                            />
                        </div>
                    )}
                </>
            )}
            {addTasksNotesDialogOpen && canUpdate && (
                <AddTasksNotesDialog
                    open={addTasksNotesDialogOpen}
                    handleClose={() => setAddTasksNotesDialogOpen(false)}
                    parentId={parentId}
                    daycareId={daycare.daycareId}
                    parentName={parentName}
                    childs={childs}
                    isRpeOrLaep={daycare.isRpeOrLaep}
                    handleRefetch={handleRefetchParent}
                />
            )}
        </div>
    )
}

export default TaskAndNotesParent
